import React, { createContext, useContext, useState, ReactNode } from "react";

type PanelContentType =
  | "system"
  | "metric"
  | "breathBiomarker"
  | "bloodBiomarker"
  // Health overview: a user-recorded biomarker (from the biomarkers resource)
  // with About/Readings tabs. Resolved live from redux by id so the panel
  // stays fresh after add/delete.
  | "userBiomarker"
  | null;

export type UserBiomarkerPanelData = {
  biomarkerID: string;
  // Set when the panel was opened from faded sample-report content (the
  // system-detail preview on a demo report): the Readings tab is locked
  // there — real value entry goes through All biomarkers / Add results.
  sampleContext?: boolean;
};

type InfluencedByFactors = {
  positive: { label: string; description: string }[];
  negative: { label: string; description: string }[];
};

export type MetricPanelData = {
  id: string;
  name: string;
  description?: string;
  value: number;
  unit: string;
  score: number;
  scoreCategory: number;
  values?: { value: number; dateCreated: string }[];
  whyItIsImportantToTrackText?: string;
  howItsMeasuredText?: string;
  influencedBy?: InfluencedByFactors;
  affectsThis?: { label: string; description: string }[];
};

export type BiomarkerPanelData = {
  id: string;
  label: string;
  fullName?: string;
  description?: string;
  value: number;
  unit: string;
  score: number;
  scoreCategory: number;
  values?: { value: number; dateCreated: string }[];
  influencedBy?: InfluencedByFactors;
  affectsThis?: { label: string; description: string }[];
};

interface ScoreSidePanelContextType {
  isOpen: boolean;
  contentType: PanelContentType;
  metricData: MetricPanelData | null;
  breathBiomarkerData: BiomarkerPanelData | null;
  bloodBiomarkerData: BiomarkerPanelData | null;
  userBiomarkerData: UserBiomarkerPanelData | null;
  highlightedBiomarkerIds: Set<string>;
  setHighlightedBiomarkerIds: (ids: Set<string>) => void;
  openPanel: (
    type: PanelContentType,
    data?: {
      metricData?: MetricPanelData;
      breathBiomarkerData?: BiomarkerPanelData;
      bloodBiomarkerData?: BiomarkerPanelData;
      userBiomarkerData?: UserBiomarkerPanelData;
    }
  ) => void;
  closePanel: () => void;
}

const ScoreSidePanelContext = createContext<
  ScoreSidePanelContextType | undefined
>(undefined);

export const ScoreSidePanelProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contentType, setContentType] = useState<PanelContentType>(null);
  const [metricData, setMetricData] = useState<MetricPanelData | null>(null);
  const [breathBiomarkerData, setBreathBiomarkerData] =
    useState<BiomarkerPanelData | null>(null);
  const [bloodBiomarkerData, setBloodBiomarkerData] =
    useState<BiomarkerPanelData | null>(null);
  const [userBiomarkerData, setUserBiomarkerData] =
    useState<UserBiomarkerPanelData | null>(null);
  const [highlightedBiomarkerIds, setHighlightedBiomarkerIds] = useState<Set<string>>(new Set());

  const openPanel = (
    type: PanelContentType,
    data?: {
      metricData?: MetricPanelData;
      breathBiomarkerData?: BiomarkerPanelData;
      bloodBiomarkerData?: BiomarkerPanelData;
      userBiomarkerData?: UserBiomarkerPanelData;
    }
  ) => {
    setContentType(type);
    setMetricData(data?.metricData ?? null);
    setBreathBiomarkerData(data?.breathBiomarkerData ?? null);
    setBloodBiomarkerData(data?.bloodBiomarkerData ?? null);
    setUserBiomarkerData(data?.userBiomarkerData ?? null);
    setIsOpen(true);
  };

  const closePanel = () => {
    setIsOpen(false);
    setContentType(null);
    setMetricData(null);
    setBreathBiomarkerData(null);
    setBloodBiomarkerData(null);
    setUserBiomarkerData(null);
  };

  return (
    <ScoreSidePanelContext.Provider
      value={{
        isOpen,
        contentType,
        metricData,
        breathBiomarkerData,
        bloodBiomarkerData,
        userBiomarkerData,
        highlightedBiomarkerIds,
        setHighlightedBiomarkerIds,
        openPanel,
        closePanel,
      }}
    >
      {children}
    </ScoreSidePanelContext.Provider>
  );
};

export const useScoreSidePanel = (): ScoreSidePanelContextType => {
  const context = useContext(ScoreSidePanelContext);
  if (!context) {
    throw new Error(
      "useScoreSidePanel must be used within ScoreSidePanelProvider"
    );
  }
  return context;
};
